'use client';

import { Button, ButtonGroup, Heading, Modal, TextPassage } from '@petsmart-ui/sparky';
import { useEffect, useState } from 'react';

import { removePayment } from '@libs/api-proxy-client';
import {
    BANNERS,
    logSentryError,
    SentryAcpCreditCardsErrors as SentryErrors,
    useAlertBanner,
    useUserSession,
} from '@libs/common-next';
import { NotFoundError } from '@libs/core';
import { trackErrorPayment, trackRemovePayment, useManagePayment } from '@libs/shared-ui';

import type { RemoveCardModalProps } from './types';

import './RemoveCardModal.scss';

export function RemoveCardModal({ modalOpen, modalClose, currentPayment }: RemoveCardModalProps) {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { setAlert } = useAlertBanner();
    const { userData } = useUserSession();
    const { syncProfileAfterPaymentChange } = useManagePayment();

    const hasActiveSubscription = userData?.payments?.some(payment => payment.hasSubscription);
    const isCardRemoveBlocked = currentPayment.isPrimary && hasActiveSubscription;

    useEffect(() => {
        if (isCardRemoveBlocked && modalOpen) {
            trackErrorPayment('This card cannot be removed');
        }
    }, [isCardRemoveBlocked, modalOpen]);

    const handleRemoveCard = async () => {
        setIsLoading(true);

        try {
            await removePayment(currentPayment.cardId);

            await syncProfileAfterPaymentChange();
            setAlert({ ...BANNERS.success });
            trackRemovePayment();
        } catch (error) {
            logSentryError(error, SentryErrors.RemoveCardModal.SubmitFailed);

            if (error instanceof NotFoundError) {
                setAlert({
                    header: 'Unable to remove card',
                    text: (error as Error).message ?? 'Unable to remove card',
                    variant: 'error',
                });
                trackErrorPayment((error as Error).message ?? 'Unable to remove card');
            } else {
                setAlert({
                    header: 'Unable to remove card',
                    text: 'Please try again later',
                    variant: 'error',
                });
                trackErrorPayment('Unable to remove card');
            }
        }

        setIsLoading(false);
        modalClose(true);
    };

    return (
        <Modal
            dismissible
            isActive={modalOpen}
            onClose={() => modalClose()}
            className="remove-card-modal"
            data-testid="remove-card-modal"
        >
            <Modal.Header>
                <Heading tagName="h6" data-testid="remove-card-modal-heading">
                    Remove card
                </Heading>
            </Modal.Header>
            <Modal.Body>
                {isCardRemoveBlocked && (
                    <Heading size="body-lg-bold" tagName="h6" data-testid="remove-card-modal-subheading">
                        This card cannot be removed
                    </Heading>
                )}
                <TextPassage
                    className={isCardRemoveBlocked ? 'card-remove-blocked' : ''}
                    size={isCardRemoveBlocked ? undefined : 'lg'}
                    data-testid="remove-card-modal-text"
                >
                    {isCardRemoveBlocked
                        ? 'Since you have an active subscription, a default card is required on your profile. Set another card as the default payment to remove this card.'
                        : 'Are you sure you want to remove this card from your account?'}
                </TextPassage>
            </Modal.Body>
            <Modal.Footer>
                <ButtonGroup align="right">
                    {isCardRemoveBlocked ? (
                        <Button
                            variant="primary"
                            text="Got it"
                            onClick={() => modalClose()}
                            size="lg"
                            data-testid="got-it-btn"
                        />
                    ) : (
                        <>
                            <Button
                                text="Keep card"
                                onClick={() => modalClose()}
                                size="lg"
                                data-testid="keep-card-btn"
                            />
                            <Button
                                className="danger-btn"
                                variant="primary"
                                text="Remove card"
                                onClick={handleRemoveCard}
                                loading={isLoading}
                                disabled={isLoading}
                                size="lg"
                                data-testid="remove-card-btn"
                            />
                        </>
                    )}
                </ButtonGroup>
            </Modal.Footer>
        </Modal>
    );
}
